import { useState, useEffect, useMemo } from 'react';
import { MapPin, AlertTriangle, Search, Loader2 } from 'lucide-react';
import { Input } from '@paulownia/shared/components/ui/input';
import { listActiveMarkets } from '@paulownia/shared/services/markets';
import type { Market, DayOfWeek } from '@paulownia/shared/types';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';
import type { SelectedMarket } from '../hooks/useOnboardingState';
import { DaySelector } from '../components/DaySelector';
import { MarketCard } from '../components/MarketCard';

interface MarketSelectionStepProps {
  selectedMarkets: SelectedMarket[];
  maxMarkets: number;
  canSelectMore: boolean;
  onAddMarket: (market: Market) => void;
  onRemoveMarket: (marketId: string) => void;
  isMarketSelected: (marketId: string) => boolean;
  onRequestMarket: () => void;
}

/**
 * Step 2: Market selection
 *
 * Allows users to select up to 3 markets (free tier) where they sell.
 * Markets are organized by day of week with search functionality.
 */
export function MarketSelectionStep({
  selectedMarkets,
  maxMarkets,
  canSelectMore,
  onAddMarket,
  onRemoveMarket,
  isMarketSelected,
  onRequestMarket,
}: MarketSelectionStepProps) {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(6); // Default to Saturday
  const [searchQuery, setSearchQuery] = useState('');

  // Load markets on mount
  useEffect(() => {
    async function loadMarkets() {
      try {
        setIsLoading(true);
        setError(null);
        const activeMarkets = await listActiveMarkets();
        setMarkets(activeMarkets);

        // If no markets on Saturday, find the first day with markets
        const hasMarketsOnDay = (day: DayOfWeek) =>
          activeMarkets.some((m) => m.dayOfWeek === day);

        if (!hasMarketsOnDay(6)) {
          const days: DayOfWeek[] = [1, 2, 3, 4, 5, 6, 7];
          const firstDayWithMarkets = days.find(hasMarketsOnDay);
          if (firstDayWithMarkets) {
            setSelectedDay(firstDayWithMarkets);
          }
        }
      } catch (err) {
        console.error('Failed to load markets:', err);
        setError('Impossible de charger les marchés. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    }

    loadMarkets();
  }, []);

  // Count selected markets by day
  const marketCountsByDay = useMemo(() => {
    const counts: Record<DayOfWeek, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
    };

    selectedMarkets.forEach((sm) => {
      const day = sm.market.dayOfWeek;
      counts[day] = (counts[day] || 0) + 1;
    });

    return counts;
  }, [selectedMarkets]);

  // Filter markets by selected day and search query
  const filteredMarkets = useMemo(() => {
    let filtered = markets.filter((m) => m.dayOfWeek === selectedDay);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (m) =>
          m.city.toLowerCase().includes(query) ||
          m.address.toLowerCase().includes(query)
      );
    }

    // Sort by city name
    return filtered.sort((a, b) => a.city.localeCompare(b.city));
  }, [markets, selectedDay, searchQuery]);

  // Handle market toggle
  const handleMarketToggle = (market: Market) => {
    if (isMarketSelected(market.id)) {
      onRemoveMarket(market.id);
    } else {
      onAddMarket(market);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <MapPin className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Vos marchés</h2>
          <p className="text-sm text-gray-500">
            Sélectionnez les marchés où vous êtes présent
          </p>
        </div>
      </div>

      {/* Free tier banner */}
      <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">
            Offre gratuite : {maxMarkets} marchés maximum
          </p>
          <p className="text-sm text-amber-700 mt-1">
            Contactez-nous pour augmenter cette limite.
          </p>
        </div>
      </div>

      {/* Selection count */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <MapPin className="h-4 w-4" />
        <span>
          {selectedMarkets.length}/{maxMarkets} marchés sélectionnés
        </span>
        {!canSelectMore && (
          <span className="text-amber-600 font-medium">(limite atteinte)</span>
        )}
      </div>

      {/* Day selector */}
      <DaySelector
        selectedDay={selectedDay}
        onDayChange={setSelectedDay}
        marketCountsByDay={marketCountsByDay}
      />

      {/* Day label */}
      <h3 className="font-medium text-gray-900">
        Marchés du {DAY_OF_WEEK_LABELS[selectedDay]}
      </h3>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Rechercher par ville..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Market list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
          <span className="ml-2 text-gray-600">Chargement des marchés...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      ) : filteredMarkets.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <div className="text-gray-400 mb-2">
            <MapPin className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-gray-600">
            {searchQuery.trim()
              ? 'Aucun marché ne correspond à votre recherche.'
              : `Aucun marché disponible le ${DAY_OF_WEEK_LABELS[selectedDay].toLowerCase()}.`}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Essayez un autre jour ou demandez l'ajout de votre marché.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMarkets.map((market) => (
            <MarketCard
              key={market.id}
              market={market}
              isSelected={isMarketSelected(market.id)}
              isDisabled={!canSelectMore}
              onToggle={() => handleMarketToggle(market)}
            />
          ))}
        </div>
      )}

      {/* Request new market button */}
      <button
        type="button"
        onClick={onRequestMarket}
        className="w-full py-3 px-4 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
      >
        Mon marché n'est pas listé
      </button>

      {/* Validation hint */}
      {selectedMarkets.length === 0 && (
        <p className="text-sm text-amber-600 flex items-center gap-1">
          <AlertTriangle className="h-4 w-4" />
          Sélectionnez au moins un marché pour continuer
        </p>
      )}
    </div>
  );
}
